import { useCaps } from "../CapsProvider";
import { useUi } from "../../../state/editor/useEditor";
import { useViewer } from "../../../state/ViewerProvider";
import { RAIL_W, type CardInset } from "../layout";

/**
 * The per-pane lock switches for the split viewport (v60, axis-locked at v61).
 *
 * One small button in each pane's top-left corner. A locked pane shows the world
 * axis it orbits about (X / Y / Z) and unlocks into a free 3-D view; an unlocked
 * pane shows 3D and locks back to the axis it came from.
 *
 * ## Why these are host DOM and not drawn in the viewer
 *
 * The canvas is a cross-origin iframe, so a click that lands ON it never
 * reaches the host — which is why `setDofFocus` had to be armed viewer-side in
 * v59. The opposite is also true and is what makes this work: a host element
 * sitting ON TOP of the iframe gets its own clicks normally. The surrounding
 * chrome is `pointer-events-none` precisely so orbit passes through the gaps,
 * and these buttons opt back in for their own few pixels.
 *
 * They are positioned from the card box, which is the same rect the viewer
 * splits into quarters, so the switches sit exactly where the pane corners are.
 */

const PANES = [
  { id: "topLeft", axis: "x", label: "X", row: 0, col: 0 },
  { id: "topRight", axis: "y", label: "Y", row: 0, col: 1 },
  { id: "bottomLeft", axis: "z", label: "Z", row: 1, col: 0 },
  { id: "bottomRight", axis: "perspective", label: "3D", row: 1, col: 1 },
] as const;

type PaneId = (typeof PANES)[number]["id"];
type PaneMode = "x" | "y" | "z" | "perspective";

export function ViewportPaneSwitches({ inset }: { inset: CardInset }) {
  const caps = useCaps();
  const [ui, patchUi] = useUi();
  const { client } = useViewer();

  // v61 values need a v61 viewer; a v60 one cannot build an axis-locked pane.
  if (!caps.viewportAxisLockV61 || ui.viewportLayout !== "quad") return null;

  const toggle = (id: PaneId, axis: PaneMode) => {
    const current = (ui.paneModes?.[id] ?? axis) as PaneMode;
    // A locked pane unlocks to perspective; an unlocked one locks back to the
    // axis this slot represents, so the layout never loses its Top/Front/Right
    // identity no matter how many times it is flipped.
    const next: PaneMode = current === "perspective" ? axis : "perspective";
    patchUi({ paneModes: { ...(ui.paneModes ?? {}), [id]: next }, activePane: id });
    void client.ring.setViewportPaneMode(id, next);
  };

  // Rhino draws a hairline between its viewports; without one the four renders
  // read as a single picture with gaps in it. Host DOM rather than pixels in the
  // canvas: the card is full-bleed now, so a cross at the midpoint lines up with
  // the quarters the viewer scissors, and it stays crisp at any pixel ratio.
  const divider = "pointer-events-none absolute bg-ed-border";
  return (
    <>
      <div
        className={divider}
        style={{ left: inset.left, right: inset.right, top: `calc((100% - ${inset.top}px - ${inset.bottom}px) / 2 + ${inset.top}px)`, height: 1 }}
      />
      <div
        className={divider}
        style={{ top: inset.top, bottom: inset.bottom, left: `calc((100% - ${inset.left}px - ${inset.right}px) / 2 + ${inset.left}px)`, width: 1 }}
      />
      {PANES.map((p) => {
        const mode = (ui.paneModes?.[p.id] ?? p.axis) as PaneMode;
        const unlocked = mode === "perspective";
        const active = ui.activePane === p.id;
        // Quarters of the card box. `calc` keeps this exact at any width
        // without the chrome having to measure the canvas itself.
        const halfW = `calc((100% - ${inset.left}px - ${inset.right}px) / 2)`;
        // ⚠️ Clear the rails. They overlay the canvas at `left-0`/`right-0`,
        // RAIL_W wide — so a label flush to the left pane's corner sits UNDER
        // the left rail and is simply invisible. The DIVIDERS stay on the true
        // canvas midpoint: they have to match the quarters the viewer scissors,
        // and the viewer knows nothing about which chrome is open.
        const left =
          p.col === 0
            ? `${inset.left + RAIL_W + 10}px`
            : `calc(${inset.left}px + ${halfW} + 10px)`;
        const top =
          p.row === 0
            ? `${inset.top + 10}px`
            : `calc(${inset.top}px + (100% - ${inset.top}px - ${inset.bottom}px) / 2 + 10px)`;
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => toggle(p.id, p.axis as PaneMode)}
            title={
              unlocked
                ? `This pane is free-orbit 3-D — click to lock it back to the ${p.label} axis`
                : `Locked to ${p.label}: dragging rotates about the ${p.label} axis only — click to unlock into 3-D`
            }
            style={{ left, top }}
            className={`pointer-events-auto absolute z-20 flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-semibold tracking-wide transition-colors ${
              active
                ? "border-ed-accent bg-ed-active text-ed-text"
                : "border-ed-border bg-white/85 text-ed-text-2 hover:border-ed-accent hover:text-ed-accent"
            }`}
          >
            <span className="material-symbols-outlined text-[13px] leading-none">
              {unlocked ? "lock_open" : "lock"}
            </span>
            {unlocked ? "3D" : p.label}
          </button>
        );
      })}
    </>
  );
}
